import { ClientType } from "../../client/enums";
import { InstitutionEnum } from "../../institution/enums";
import { CalcType } from "../enums";
import { AttachmentConfType } from "./enums";
import {
  AttachmentConf,
  CalcSettings,
  CrashCalcSettings,
  FileAttachmentConf,
  GapCalcSettings,
  MtplCalcSettings,
  MtplCrashCalcSettings,
  PasCalcSettings,
  RealtyCalcSettings,
  TravelCalcSettings,
  UpdateAttachmentConf,
  UpdateCalcSettings,
} from "./types";

export const isCrashCalcSettings = (calcSettings: CalcSettings): calcSettings is CrashCalcSettings =>
  calcSettings.type === CalcType.CRASH;

export const isGapCalcSettings = (calcSettings: CalcSettings): calcSettings is GapCalcSettings =>
  calcSettings.type === CalcType.GAP;

export const isMtplCalcSettings = (calcSettings: CalcSettings): calcSettings is MtplCalcSettings =>
  calcSettings.type === CalcType.MTPL;

export const isMtplCrashCalcSettings = (calcSettings: CalcSettings): calcSettings is MtplCrashCalcSettings =>
  calcSettings.type === CalcType.MTPL_CRASH;

export const isPasCalcSettings = (calcSettings: CalcSettings): calcSettings is PasCalcSettings =>
  calcSettings.type === CalcType.PAS;

export const isRealtyCalcSettings = (calcSettings: CalcSettings): calcSettings is RealtyCalcSettings =>
  calcSettings.type === CalcType.REALTY;

export const isTravelCalcSettings = (calcSettings: CalcSettings): calcSettings is TravelCalcSettings =>
  calcSettings.type === CalcType.TRAVEL;

export const isFileAttachmentConf = (attachmentConf: AttachmentConf): attachmentConf is FileAttachmentConf =>
  attachmentConf.type === AttachmentConfType.STATIC || attachmentConf.type === AttachmentConfType.DYNAMIC;

export const convertAttachmentConfToUpdateAttachmentConf = (attachmentConf: AttachmentConf): UpdateAttachmentConf => {
  const result: UpdateAttachmentConf = {
    id: attachmentConf.id,
    optimisticLockVersion: attachmentConf.optimisticLockVersion,
    contractAttachmentType: attachmentConf.contractAttachmentType,
    shouldAttachToContract: attachmentConf.shouldAttachToContract,
    shouldAttachToEmail: attachmentConf.shouldAttachToEmail,
    shouldEncrypt: attachmentConf.shouldEncrypt,
    filename: attachmentConf.filename,
  };

  if (isFileAttachmentConf(attachmentConf)) {
    result.shouldAttachToCalc = attachmentConf.shouldAttachToCalc;
  }

  return result;
};

type BaseUpdateData = Pick<
  UpdateCalcSettings,
  "optimisticLockVersion" | "enabled" | "paymentDetails" | "institutionId" | "attachmentConfs"
>;

type CalcSettingsHandlerMap = {
  [K in CalcType]: (
    calcSettings: Extract<CalcSettings, { type: K }>,
    baseData: BaseUpdateData,
  ) => Extract<UpdateCalcSettings, { type: K }>;
};

const calcSettingsUpdateHandlers: CalcSettingsHandlerMap = {
  [CalcType.CRASH]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.CRASH,
    crashProductId: calcSettings.crashProduct.id,
  }),
  [CalcType.GAP]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.GAP,
    gapProductId: calcSettings.gapProduct.id,
  }),
  [CalcType.MTPL]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.MTPL,
    mtplProductId: calcSettings.mtplProduct.id,
  }),
  [CalcType.MTPL_CRASH]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.MTPL_CRASH,
    mtplCrashProductId: calcSettings.mtplCrashProduct.id,
  }),
  [CalcType.PAS]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.PAS,
    pasProductId: calcSettings.pasProduct.id,
  }),
  [CalcType.REALTY]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.REALTY,
    realtyProductId: calcSettings.realtyProduct.id,
  }),
  [CalcType.TRAVEL]: (calcSettings, baseData) => ({
    ...baseData,
    type: CalcType.TRAVEL,
    shortTermInsuranceProductId: calcSettings.shortTermInsuranceProduct.id,
    yearInsuranceProductId: calcSettings.yearInsuranceProduct.id,
    cancellationInsuranceProductId: calcSettings.cancellationInsuranceProduct.id,
  }),
};

export const convertCalcSettingsToUpdateObject = (calcSettings: CalcSettings): UpdateCalcSettings => {
  const baseData = {
    optimisticLockVersion: calcSettings.optimisticLockVersion,
    enabled: calcSettings.enabled,
    paymentDetails: calcSettings.paymentDetails,
    institutionId: calcSettings.institution.id,
    attachmentConfs: calcSettings.attachmentConfs.map(convertAttachmentConfToUpdateAttachmentConf),
  };

  if (isCrashCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.CRASH](calcSettings, baseData);
  }

  if (isGapCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.GAP](calcSettings, baseData);
  }

  if (isMtplCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.MTPL](calcSettings, baseData);
  }

  if (isMtplCrashCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.MTPL_CRASH](calcSettings, baseData);
  }

  if (isPasCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.PAS](calcSettings, baseData);
  }

  if (isRealtyCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.REALTY](calcSettings, baseData);
  }

  if (isTravelCalcSettings(calcSettings)) {
    return calcSettingsUpdateHandlers[CalcType.TRAVEL](calcSettings, baseData);
  }

  throw new Error("Unsupported calc settings type", calcSettings);
};

type CalcAttachmentFilterConfiguration = {
  fileName: string;
  institutionEnum: InstitutionEnum;
  calcType: CalcType[];
  clientType: ClientType[];
};

export const CALC_ATTACHMENT_FILTER: CalcAttachmentFilterConfiguration[] = [
  {
    fileName: "Sprievodca_poistenim_.pdf",
    institutionEnum: InstitutionEnum.ALLIANZ,
    calcType: [CalcType.MTPL, CalcType.MTPL_CRASH],
    clientType: [ClientType.NATURAL],
  },
  {
    fileName: "Sprievodca_poistenim_FLOTILA.pdf",
    institutionEnum: InstitutionEnum.ALLIANZ,
    calcType: [CalcType.MTPL, CalcType.MTPL_CRASH],
    clientType: [ClientType.SELF_EMPLOYED, ClientType.LEGAL],
  },
];

export const isCalcAttachmentFiltered = (data: {
  fileName: string;
  calcType: CalcType;
  institutionEnum?: InstitutionEnum;
  clientType?: ClientType;
}) => {
  const configuration = CALC_ATTACHMENT_FILTER.find(
    (filter) =>
      data.fileName === filter.fileName &&
      data.institutionEnum === filter.institutionEnum &&
      filter.calcType.includes(data.calcType),
  );

  if (!configuration || !data.clientType) {
    return true;
  }

  return configuration.clientType.includes(data.clientType);
};
